import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '../ui/carousel';
import mawtenAlEmaarHolding from '../../assets/client-logos/mawten-al-emaar-holding.jpeg';
import makkahAlmukarramah from '../../assets/client-logos/makkah-almukarramah.jpeg';
import alandalusCrown from '../../assets/client-logos/alandalus-crown.jpeg';
import moufrehStation from '../../assets/client-logos/moufreh-station.jpeg';
import kaust from '../../assets/client-logos/kaust.jpeg';
import dirahInn from '../../assets/client-logos/dirah-inn.jpeg';
import preciousCollar from '../../assets/client-logos/precious-collar.jpeg';
import mediTech from '../../assets/client-logos/medi-tech.jpeg';
import saudiOzone from '../../assets/client-logos/saudi-ozone.jpeg';
import mineralResourcesIndustrial from '../../assets/client-logos/mineral-resources-industrial.jpeg';
import alshamraniDrawBarTrailers from '../../assets/client-logos/alshamrani-draw-bar-trailers.jpeg';
import rouyahAlbarakahTraining from '../../assets/client-logos/rouyah-albarakah-training.jpeg';
import geometricBlackLogo from '../../assets/client-logos/geometric-black-logo.jpeg';

const ClientLogosSection = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [api, setApi] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const logos = [
    {
      nameAr: 'شركة موطن الإعمار القابضة',
      nameEn: 'Mawten Al-Emaar Holding Co.',
      src: mawtenAlEmaarHolding,
      imageClassName: 'max-h-[180px] md:max-h-[200px] scale-[1.12]'
    },
    {
      nameAr: 'مكة المكرمة',
      nameEn: 'Makkah Almukarramah',
      src: makkahAlmukarramah,
      imageClassName: 'max-h-[172px] md:max-h-[192px] scale-[1.12]'
    },
    {
      nameAr: 'الأندلس كراون',
      nameEn: 'Alandalus Crown',
      src: alandalusCrown,
      imageClassName: 'max-h-[172px] md:max-h-[192px] scale-[1.14]'
    },
    {
      nameAr: 'محطة مفرح',
      nameEn: 'Moufreh Station',
      src: moufrehStation,
      imageClassName: 'max-h-[172px] md:max-h-[192px] scale-[1.14]'
    },
    {
      nameAr: 'جامعة الملك عبدالله للعلوم والتقنية',
      nameEn: 'King Abdullah University of Science and Technology',
      src: kaust,
      imageClassName: 'max-h-[170px] md:max-h-[188px] scale-[1.08]'
    },
    {
      nameAr: 'درة إن',
      nameEn: 'Dirah Inn',
      src: dirahInn,
      imageClassName: 'max-h-[178px] md:max-h-[198px] scale-[1.14]'
    },
    {
      nameAr: 'Precious Collar',
      nameEn: 'Precious Collar',
      src: preciousCollar,
      imageClassName: 'max-h-[180px] md:max-h-[202px] scale-[1.16]'
    },
    {
      nameAr: 'Medi Tech',
      nameEn: 'Medi Tech',
      src: mediTech,
      imageClassName: 'max-h-[180px] md:max-h-[200px] scale-[1.15]'
    },
    {
      nameAr: 'الشركة السعودية للأوزون',
      nameEn: 'Saudi Ozone Company',
      src: saudiOzone,
      imageClassName: 'max-h-[182px] md:max-h-[204px] scale-[1.18]'
    },
    {
      nameAr: 'شركة الموارد المعدنية الصناعية',
      nameEn: 'Mineral Resources Industrial Co.',
      src: mineralResourcesIndustrial,
      imageClassName: 'max-h-[150px] md:max-h-[166px] scale-[1.2]'
    },
    {
      nameAr: 'مصنع الشمراني لمقطورات السحب',
      nameEn: 'Alshamrani Factory for Draw Bar Trailers',
      src: alshamraniDrawBarTrailers,
      imageClassName: 'max-h-[152px] md:max-h-[168px] scale-[1.2]'
    },
    {
      nameAr: 'رؤيا البركة للتدريب',
      nameEn: 'Rouyah Albarakah Training',
      src: rouyahAlbarakahTraining,
      imageClassName: 'max-h-[170px] md:max-h-[188px] scale-[1.12]'
    },
    {
      nameAr: 'الشعار الأسود الهندسي',
      nameEn: 'Geometric Logo',
      src: geometricBlackLogo,
      imageClassName: 'max-h-[146px] md:max-h-[162px] scale-[1.08]'
    }
  ];

  useEffect(() => {
    if (!api || isHovered) {
      return undefined;
    }

    const interval = setInterval(() => {
      api.scrollNext();
    }, 3200);

    return () => clearInterval(interval);
  }, [api, isHovered]);

  return (
    <section id="client-logos" className="py-24 px-4 bg-white relative z-10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14" dir={isArabic ? 'rtl' : 'ltr'}>
          <h2 className="text-4xl sm:text-5xl font-bold text-wazin-navy mb-5">
            {isArabic ? 'عملاء وثقوا بنا' : 'Clients Who Trusted Us'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {isArabic
              ? 'نفخر بثقة عدد من الجهات والمنشآت التي اختارت وازن شريكًا قانونيًا لأعمالها.'
              : 'We are proud of the trust placed in Wazin by organizations and businesses that chose us as their legal partner.'}
          </p>
        </div>

        <div
          className="relative px-12 md:px-16 xl:px-20"
          dir={isArabic ? 'rtl' : 'ltr'}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              loop: true,
              direction: isArabic ? 'rtl' : 'ltr'
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-6">
              {logos.map((logo) => (
                <CarouselItem key={logo.nameEn} className="pl-6 basis-full md:basis-1/2 xl:basis-1/3">
                  <div className="h-full flex items-center justify-center">
                    <div className="w-full min-h-[220px] md:min-h-[240px] flex items-center justify-center px-5 py-7 md:px-8 md:py-9 transition-transform duration-300 hover:-translate-y-1">
                      <img
                        src={logo.src}
                        alt={isArabic ? logo.nameAr : logo.nameEn}
                        className={`w-full object-contain mix-blend-multiply opacity-70 transition-all duration-300 hover:opacity-100 hover:scale-[1.04] ${logo.imageClassName}`}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious
              className="left-0 md:left-2 h-11 w-11 border-[#A27849]/35 bg-white text-[#8C653A] shadow-[0_10px_25px_rgba(15,23,42,0.08)] hover:bg-[#A27849] hover:text-white disabled:opacity-35"
            />
            <CarouselNext
              className="right-0 md:right-2 h-11 w-11 border-[#A27849]/35 bg-white text-[#8C653A] shadow-[0_10px_25px_rgba(15,23,42,0.08)] hover:bg-[#A27849] hover:text-white disabled:opacity-35"
            />
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default ClientLogosSection;
